/**
 * The icon set the design references.
 *
 * The design document pulls these from a licensed UI kit
 * (`CommunityMoneyTransferUIKit.Icon`). That kit is not redistributable, so the
 * names it uses are reproduced here as original 24-grid stroke icons with the
 * same optical weight. The *names* are the contract — `Home2`, `Card3` and
 * `Recipients` read oddly on their own and are kept verbatim so a designer
 * looking at the canvas and a developer looking at this file are talking about
 * the same glyph.
 */

export const ICON_NAMES = [
  'Home2',
  'Wallet',
  'Transfer',
  'Card3',
  'Recipients',
  'Chart',
  'Settings',
  'Search',
  'Bell',
  'Contactless',
  'Chip',
] as const

export type IconName = (typeof ICON_NAMES)[number]

const PATHS: Record<IconName, React.ReactNode> = {
  Home2: (
    <>
      <path d="M3.5 10.2 12 3.5l8.5 6.7" />
      <path d="M5.5 9.2V20h13V9.2" />
      <path d="M9.75 20v-5.5h4.5V20" />
    </>
  ),
  Wallet: (
    <>
      <path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h11a2 2 0 0 1 2 2v1" />
      <rect x="3.5" y="7.5" width="17" height="11" rx="2.2" />
      <path d="M20.5 11.5h-3.6a2 2 0 0 0 0 4h3.6" />
    </>
  ),
  Transfer: (
    <>
      <path d="M4 8.5h13" />
      <path d="M13.5 5 17 8.5 13.5 12" />
      <path d="M20 15.5H7" />
      <path d="M10.5 12 7 15.5 10.5 19" />
    </>
  ),
  Card3: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.4" />
      <path d="M2.5 9.75h19" />
      <path d="M6 15h3.5" />
    </>
  ),
  Recipients: (
    <>
      <circle cx="9" cy="8.5" r="3.3" />
      <path d="M3.2 19.2c.5-3.1 3-5 5.8-5s5.3 1.9 5.8 5" />
      <path d="M16 5.6a3.3 3.3 0 0 1 0 5.9" />
      <path d="M17.6 14.6c1.7.7 2.9 2.3 3.2 4.6" />
    </>
  ),
  Chart: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M7.5 16.5v-4" />
      <path d="M12 16.5V8" />
      <path d="M16.5 16.5v-6.5" />
    </>
  ),
  Settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.6a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a1.9 1.9 0 1 1-3.8 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3.4a1.9 1.9 0 1 1 0-3.8h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V3.4a1.9 1.9 0 1 1 3.8 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a1.9 1.9 0 1 1 0 3.8h-.1a1.6 1.6 0 0 0-1.5 1Z" />
    </>
  ),
  Search: (
    <>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.5 15.5 4 4" />
    </>
  ),
  Bell: (
    <>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9" />
      <path d="M13.7 19.5a2 2 0 0 1-3.4 0" />
    </>
  ),
  Contactless: (
    <>
      <path d="M7.5 8.5a5.5 5.5 0 0 1 0 7" />
      <path d="M11 6a9 9 0 0 1 0 12" />
      <path d="M14.5 3.5a12.5 12.5 0 0 1 0 17" />
    </>
  ),
  Chip: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="2.6" />
      <path d="M3.5 10.5h4.2v3H3.5" />
      <path d="M20.5 10.5h-4.2v3h4.2" />
      <path d="M7.7 6v12" />
      <path d="M16.3 6v12" />
    </>
  ),
}

export interface IconProps {
  readonly name: IconName
  readonly size?: number
  readonly className?: string
}

export function Icon({ name, size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(className === undefined ? {} : { className })}
    >
      {PATHS[name]}
    </svg>
  )
}
